import { faBookmark } from "@fortawesome/pro-regular-svg-icons/faBookmark";
import { faBookmark as faBookmarked } from "@fortawesome/pro-solid-svg-icons/faBookmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Typography } from "@mui/material";
import { MprcRequestIndicator } from "components";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { TrainingUserInteractionOption } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

///////////////////////////////////////////////////////////////////////////////

interface IProps {
    trainingId: string;
    option: TrainingUserInteractionOption.RemoveBookmark | TrainingUserInteractionOption.Bookmark;
    onClose: (success: boolean) => void;
}

export const DoTrainingBoomark = ({ trainingId, option, onClose }: IProps) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const [updating, setUpdating] = useState<boolean>(false);

    const doBookmark = () => {
        setUpdating(true);
        api.v1.lmsStudent.userInteraction({
            trainingId,
            option
        })
            .then((result) => {
                if (result.data === true) {
                    enqueueSnackbar(t(option == TrainingUserInteractionOption.Bookmark
                        ? "Training.Bookmark.BookmarkSuccessMessage"
                        : "Training.Bookmark.RemoveBookmarkSuccessMessage"), { variant: "success" });
                }
                else {
                    enqueueSnackbar(t(option == TrainingUserInteractionOption.Bookmark
                        ? "Training.Bookmark.BookmarkErrorMessage"
                        : "Training.Bookmark.RemoveBookmarkErrorMessage"), { variant: "error" });
                }

                onClose(result.data);
            })
            .catch(MprcRequestSnackbarErrorReport)
            .finally(() => setUpdating(false));
    };

    useEffect(() => {
        doBookmark();
    }, []);

    return <>{updating ? <MprcRequestIndicator visible={updating} type="post" /> : <></>}</>;
};

///////////////////////////////////////////////////////////////////////////////

interface IBookmarkButtonProps {
    trainingId: string;
    bookmarked?: boolean;
    readonly?: boolean;
    onChange?: (bookmarked: boolean) => void;
}

export const TrainingBookmarkButton = ({ trainingId, bookmarked = false, readonly = false, onChange }: IBookmarkButtonProps) => {

    const { t } = useTranslation("lms");
    const [open, setOpen] = useState<boolean>(false);

    return (
        <>
            {open &&
                <DoTrainingBoomark
                    trainingId={trainingId}
                    option={bookmarked ? TrainingUserInteractionOption.RemoveBookmark : TrainingUserInteractionOption.Bookmark}
                    onClose={
                        (success) => {
                            success && onChange && onChange(!bookmarked);
                            setOpen(false);
                        }
                    }
                />
            }
            <Typography
                variant={bookmarked ? "iconTextHighlight" : "iconText"}
                title={bookmarked ? t("Training.Bookmark.BookmarkedTooltip") : t("Training.Bookmark.BookmarkTooltip")}
                component="div"
            >
                <IconButton
                    color="inherit"
                    disabled={open || readonly}
                    onClick={() => setOpen(true)}
                >
                    <FontAwesomeIcon icon={bookmarked ? faBookmarked : faBookmark} fixedWidth />
                </IconButton>
            </Typography>
        </>
    );
};

export const TrainingBookmarkCardButton = ({ trainingId, bookmarked = false, readonly = false, onChange }: IBookmarkButtonProps) => {

    const { t } = useTranslation("lms");
    const [open, setOpen] = useState<boolean>(false);

    return (
        <>
            {open &&
                <DoTrainingBoomark
                    trainingId={trainingId}
                    option={bookmarked ? TrainingUserInteractionOption.RemoveBookmark : TrainingUserInteractionOption.Bookmark}
                    onClose={
                        (success) => {
                            success && onChange && onChange(!bookmarked);
                            setOpen(false);
                        }
                    }
                />
            }
            <Typography variant="iconText"
                color={bookmarked ? "highlight.main" : "muted.main"}
                title={bookmarked ? t("Training.Bookmark.BookmarkedTooltip") : t("Training.Bookmark.BookmarkTooltip")}
                component="div"
            >
                <IconButton
                    color="inherit"
                    disabled={open || readonly}
                    onClick={() => setOpen(true)}
                >
                    <FontAwesomeIcon icon={bookmarked ? faBookmarked : faBookmark} fixedWidth />
                </IconButton>
            </Typography>
        </>
    );
};
